import { Helmet } from "react-helmet-async";

const SITE_NAME = "ResumeLab";
const SITE_URL = "https://resumelab.co.ke";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.svg`;

/**
 * Per-page SEO + Open Graph + Twitter card.
 *
 * Props:
 *  - title       (string)  short page title, e.g. "Pricing"
 *  - description (string)  ~150-160 char meta description
 *  - path        (string)  pathname for canonical, e.g. "/pricing"
 *  - image       (string?) absolute URL for og:image, optional
 *  - noindex     (bool?)   set true on auth-gated routes
 *  - jsonLd      (object?) optional structured data
 *  - locales     (string[]?) extra hreflang codes beyond the global default set
 *                (e.g. ["en-ae", "en-sa"] for a UAE/Saudi-focused post)
 */

// Global English locales we serve — covers our top 15+ ranked markets. Every page
// gets these; country-specific blog posts can pass additional `locales` for
// stronger geo-signals on that post.
const DEFAULT_LOCALES = [
  "en",       // Global English
  "en-us",    // United States
  "en-gb",    // United Kingdom
  "en-ke",    // Kenya (primary market)
  "en-ng",    // Nigeria
  "en-za",    // South Africa
  "en-gh",    // Ghana
  "en-eg",    // Egypt
  "en-ae",    // United Arab Emirates
  "en-sa",    // Saudi Arabia
  "en-in",    // India
  "en-pk",    // Pakistan
  "en-ph",    // Philippines
  "en-ca",    // Canada
  "en-au",    // Australia
  "en-sg",    // Singapore
  "pt-br",    // Brazil (Portuguese content)
  "de-de",    // Germany (Lebenslauf content)
  "es-mx",    // Mexico (Spanish content)
];

export default function SEO({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
  locales,
}) {
  const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — AI Resume Checker, ATS Scanner & Recruiter-Grade Rewrites`;
  const url = `${SITE_URL}${path}`;
  // Merge default + page-specific locales, dedupe while preserving order
  const allLocales = Array.from(new Set([...DEFAULT_LOCALES, ...(locales || [])]));
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      {/* Hreflang — global English + top country-specific markets we serve */}
      {allLocales.map((lang) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* RSS auto-discovery — feed readers + crawlers find the blog feed */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="ResumeLab Blog — Resume & Career Strategy"
        href={`${SITE_URL}/blog/rss.xml`}
      />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_KE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@resumelab_co" />
      <meta name="twitter:creator" content="@resumelab_co" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
}
